import React, { useState, useEffect, useRef, useCallback } from "react";

import {
  IPanel,
  ResizeGroup,
  Persona,
  PersonaSize,
  TooltipHost,
  PersonaInitialsColor,
  IconButton,
  Toggle,
} from "@fluentui/react";
import {
  Header,
  HeaderButton,
  usePanels,
  useResizeGroup,
} from "@coherence-design-system/header";
import { ProfileCenter } from "@coherence-design-system/profile-center";
import { Template } from "@coherence-design-system/slot";
//import MS_LOGO from "../../assets/ms-logo-white.png";
import MS_LOGO_WHITE from "../../assets/ms-logo-white.png";
// Note: If you don't have a black logo asset, create one or keep using white for both themes
//import { useAppSelector } from "../../hooks/redux";
import { useAppSelector, useAppDispatch } from "../../hooks/redux";
import { toggleTheme } from "../../store/reducers/themeSlice";

import "./index.css";
import { useMsal } from "@azure/msal-react";

interface IAppHeaderProps {
  navMenuCallback: (isMenuExpanded: boolean) => void;
}

const AppHeader = (props: IAppHeaderProps) => {
  const { navMenuCallback } = props;
  const profileCenterRef = useRef<IPanel>(null);
  const { currentPanel, openPanel, closePanel } = usePanels();
  const userDetails = useAppSelector((state) => state.auth.userDetails);
  const { instance } = useMsal();
  const dispatch = useAppDispatch();

  // Get current theme from Redux store
  const currentTheme = useAppSelector((state) => state.theme.theme);

  const showHamburger = true;

  // Profile Center
  const profileProps = {
    persona: {
      text: userDetails && userDetails.name ? userDetails.name : "",
      tertiaryText:
        userDetails && userDetails.userName ? userDetails.userName : "",
    },
    actions: [
      {
        children: (
          <div style={{ color: "#000" }}>
            <span style={{ fontWeight: "bold" }}>Ownership SG:</span>{" "}
            <span>
              {userDetails &&
                userDetails.sgNames &&
                userDetails.sgNames.length > 0
                ? userDetails.sgNames.join(", ")
                : ""}
            </span>
          </div>
        ),
      },
      {
        children: (
          <div style={{ color: "#000" }}>
            <span style={{ fontWeight: "bold" }}>Permission Level:</span>{" "}
            <span>
              {" "}
              {userDetails && userDetails.permissionRoles.length > 0
                ? userDetails.permissionRoles.join(", ")
                : ""}{" "}
            </span>
          </div>
        ),
      },
      {
        children: (
          <button onClick={() => instance.logoutRedirect()}>Sign Out</button>
        ),
      },
    ],
  };

  const [isMenuExpanded, setIsMenuExpanded] = useState(true);
  // const [checked, setChecked] = useState(false);

  // Initialize toggle state based on current theme from Redux store
  useEffect(() => {
    // No local state needed - use Redux state directly
  }, []);

  const handleChange = () => {
    dispatch(toggleTheme());
    // Refresh the page to ensure all components are properly themed
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const URLS = {
    FEATURE_REQUEST: "https://azurecsi.visualstudio.com/AzStandard/_workitems/create/Feature?templateId=19b96aeb-b8d5-474a-ac9e-089c19fe2cdd&ownerId=aa6f0730-5845-48c9-a284-b53688bc777e",
    BUG_REPORT: "https://azurecsi.visualstudio.com/AzStandard/_workitems/create/Bug?templateId=bcc2a985-d8d9-4685-824a-be562e6f5f9f&ownerId=aa6f0730-5845-48c9-a284-b53688bc777e",
    LAB_SUPPORT: "https://dev.azure.com/CHIELabs/CHIE%20Labs"
  };

  const _items = [
    {
      key: "feature",
      icon: "Ticket",
      ariaLabel: "Request a feature",
      text: "Request a feature",
      onClick: () => {
        window.open(URLS.FEATURE_REQUEST, "_blank", "noopener,noreferrer");
      },
      iconProps: { iconName: "Ticket" },
    },
    {
      key: "bug",
      icon: "Bug",
      iconProps: { iconName: "Bug" },
      ariaLabel: "Report a bug",
      text: "Report a bug",
      onClick: () => {
        window.open(URLS.BUG_REPORT, "_blank", "noopener,noreferrer");
      },
    },
    {
      key: "feedback",
      icon: "Feedback",
      iconProps: { iconName: "Feedback" },
      ariaLabel: "Lab & Logistics Support",
      text: "Lab & Logistics Support",
      onClick: () => {
        window.open(URLS.LAB_SUPPORT, "_blank", "noopener,noreferrer");
      },
    },
  ];

  const formattedData = {
    primary: _items,
    overflow: [],
  };
  const { onReduceData, onGrowData, onRenderData } = useResizeGroup({
    closePanel,
    currentPanel,
  });

  const handleHamburgerClick = useCallback(() => {
    navMenuCallback(!isMenuExpanded);
    setIsMenuExpanded(!isMenuExpanded);
  }, [navMenuCallback, isMenuExpanded]);

  useEffect(() => {
    const button = document.getElementById("COHERENCE_HAMBURGER_BUTTON");
    if (button) {
      button.addEventListener("click", handleHamburgerClick);
    }

    return () => {
      if (button) {
        button.removeEventListener("click", handleHamburgerClick);
      }
    };
  }, [handleHamburgerClick]);

  return (
    <React.Fragment>
      <div className="Cheader-container" role="banner">
        <Header
          title={
            <div className="Cheader-title-div">
              <img src={MS_LOGO_WHITE} alt="Logo" className="Clogo-style" />
              <span className="Clogo-text">SCHIE Lab Portal</span>
            </div>
          }
          showHamburger={showHamburger}
          styles={{
            root: {
              // backgroundColor: "#0078d4",
              backgroundColor: "var(--header-background, #eef0f1)",
            },
          }}
        >
          <Template slot="collapsible-triggers">
            <ResizeGroup
              styles={{ root: { background: "transparent", height: "100%" } }}
              data={formattedData}
              onReduceData={onReduceData}
              onGrowData={onGrowData}
              onRenderData={onRenderData}
            />
          </Template>
          <Template slot="static-triggers">
            <div className="header-icons-container">
              {/* Theme toggle first, then profile */}
              <TooltipHost content={"Toggle theme"} id={"theme-toggle"}>
                <HeaderButton
                  aria-labelledby={"theme-toggle"}
                  className="theme-toggle-button">
                  <label className="switch">
                    <input type="checkbox" checked={currentTheme === 'dark'} onChange={handleChange} />
                    <span className="slider"></span>
                  </label>
                </HeaderButton>
              </TooltipHost>
              <TooltipHost content={"profile"} id={"profile"}>
                <HeaderButton
                  onClick={() => openPanel(profileCenterRef)}
                  aria-labelledby={"profile"}
                >
                  <Persona
                    initialsColor={PersonaInitialsColor.darkBlue}
                    hidePersonaDetails={true}
                    size={PersonaSize.size28}
                    {...profileProps.persona}
                  />
                </HeaderButton>
              </TooltipHost></div>
          </Template>
        </Header>
      </div>
      <React.Fragment>
        <ProfileCenter
          componentRef={profileCenterRef}
          isLightDismiss={true}
          onDismiss={() => closePanel()}
          {...profileProps}
        />
      </React.Fragment>
    </React.Fragment>
  );
};

export default AppHeader;
